import type { CommonAnalytics } from '../Platform';
import type { PlatformAPI } from '../PlatformLine';
import AnalyticsProperties from './AnalyticsProperties';
import analytics from './index';

export type Timing = { requestMs: number };
export type Tries = { requestTries: number };

export * from './adEvents';
export * from './communityEvents';

export function trackABTestAssigned(payload: {
  test: string;
  bucket: string;
  newPlayersOnly: boolean;
}): void {
  analytics.pushEvent('ABTestAssigned', payload);
}

export function trackCanCreateShortcutAsyncFailure(
  error: Error,
  payload: Timing,
): void {
  analytics.pushError('CanCreateShortcutAsyncFailure', error, payload);
}

export function trackCanSubscribeBotSuccess(
  payload: { canSubscribe: boolean } & Timing & Tries,
): void {
  analytics.pushEvent('CanSubscribeBotSuccess', payload);
}

export function trackCanSubscribeBotFailed(
  error: Error,
  payload: Partial<Timing & Tries>,
): void {
  analytics.pushError('CanSubscribeBotFailed', error, payload);
}

export function trackCheckCanPlayerMatchAsyncFailure(
  error: Error,
  payload: Timing,
): void {
  analytics.pushError('CheckCanPlayerMatchAsyncFailure', error, payload);
}

type ChooseAsyncPayload = {
  feature: string;
  oldContextID: string;
  filterMaxSize?: boolean;
  filterMinSize?: boolean;
  filterNewContext?: boolean;
  filterNewPlayers?: boolean;
};

export function trackChooseAsync(payload: ChooseAsyncPayload): void {
  analytics.pushEvent('ChooseAsync', payload);
}

export function trackChooseAsyncSuccess(
  payload: ChooseAsyncPayload & { newContextID: string } & Timing,
): void {
  analytics.pushEvent('ChooseAsyncSuccess', payload);
}

export function trackChooseAsyncFailed(
  error: Error,
  payload: ChooseAsyncPayload & Timing,
): void {
  analytics.pushError('ChooseAsyncFailed', error, payload);
}

export type ConsumePurchasePayload = {
  /** The identifier of the product to purchase */
  productID: string;

  /** An optional payload included in the purchase's signed request. */
  developerPayload?: string;

  /** Whether purchase was already provisioned when consuming. */
  alreadyProvisioned: boolean;

  /** Whether consuming an outstanding purchase at launch, or at the time of purchase. */
  consumedAtLaunch: boolean;
};

export function trackConsumePurchaseAsync(
  payload: ConsumePurchasePayload & Timing,
): void {
  analytics.pushEvent('ConsumePurchaseAsyncSuccess', payload);
}

export function trackConsumePurchaseAsyncFailure(
  error: Error,
  payload: ConsumePurchasePayload & Partial<Timing>,
): void {
  analytics.pushError('ConsumePurchaseAsyncFailure', error, payload);
}

export function trackConsumingPurchasesFailed(error: Error): void {
  analytics.pushError('consumingPurchasesFailed', error);
}

type CreateAsyncPayload = CommonAnalytics & {
  oldContextID: string;
  receiverID: string;
};

export function trackCreateAsync(payload: CreateAsyncPayload): void {
  analytics.pushEvent('CreateAsync', payload);
}

export function trackCreateAsyncSuccess(
  payload: CreateAsyncPayload & {
    newContextID: string;
  } & Timing,
): void {
  analytics.pushEvent('CreateAsyncSuccess', payload);
}

export function trackCreateAsyncFailed(
  error: Error,
  payload: CreateAsyncPayload & Timing,
): void {
  analytics.pushError('CreateAsyncFailed', error, payload);
}

export function trackCreateShortcutSuccess(payload: Timing): void {
  analytics.pushEvent('CreateShortcutSuccess', payload);
}

export function trackCreateShortcutFailure(
  error: Error,
  payload: Timing,
): void {
  analytics.pushError('CreateShortcutFailure', error, payload);
}

export function trackDebugSendMessageAsync(
  payload: AnalyticsProperties.SocialEventProperties & {
    lineImageHash: string;
  },
): void {
  analytics.pushEvent('DebugSendMessageAsync', payload);
}

export function trackDebugUploadMessageImage(
  payload: UploadMessageImagePayload,
): void {
  analytics.pushEvent('DebugUploadMessageImage', payload);
}

export function trackEntryFailed(error: Error): void {
  analytics.pushError('EntryFailed', error);
}

export function trackFirstScreenTap(payload: {
  occasion: 'sessionStart';
}): void {
  analytics.pushEvent('FirstScreenTap', payload);
}

export function trackFirstButtonTap(
  payload: { occasion: 'sessionStart' } & AnalyticsProperties.AnyPayload,
): void {
  analytics.pushEvent('FirstButtonTap', payload);
}

export function trackPinch(
  payload: {
    resource: string;
    trigger: string;
    allTimeCount: number;
    allTimeCountForResource: number;
    sessionCount: number;
    sessionCountForResource: number;
  } & AnalyticsProperties.AnyPayload,
): void {
  analytics.pushEvent('Pinch', payload);
}

export function trackGetASIDAsyncFailure(
  error: Error,
  payload: Timing & Tries,
): void {
  analytics.pushError('GetASIDAsyncFailure', error, payload);
}

export function trackGetCatalogAsyncFailed(
  error: Error,
  payload?: Timing & Tries,
): void {
  analytics.pushError('getCatalogAsyncFailed', error, payload);
}

export function trackGetConnectedPlayersSuccess(
  payload: {
    playerCount: number;
  } & Timing &
    Tries,
): void {
  analytics.pushEvent('GetConnectedPlayersSuccess', payload);
}

export function trackGetConnectedPlayersFailed(
  error: Error,
  payload: Timing & Tries,
): void {
  analytics.pushError('GetConnectedPlayersFailed', error, payload);
}

export function trackGetContextPlayersAsyncFailure(
  error: Error,
  payload: Timing,
): void {
  analytics.pushError('GetContextPlayersAsyncFailure', error, payload);
}

export function trackGetDataAsyncFailed(
  error: Error,
  payload: Timing & Tries,
): void {
  analytics.pushError('GetDataAsyncFailed', error, payload);
}

export function trackGetEntryPointAsyncFailure(
  error: Error,
  payload: Timing & Tries,
): void {
  analytics.pushError('GetEntryPointAsyncFailure', error, payload);
}

export function trackGetEntryTimestampsFailure(
  error: Error,
  payload?: { receiverID: string; entryTimestamps: unknown },
): void {
  analytics.pushError('GetEntryTimestampsFailure', error, payload);
}

export function trackGetPlayerSquadsAsyncFailure(
  error: Error,
  payload: Timing & Tries,
): void {
  analytics.pushError('GetPlayerSquadsAsyncFailure', error, payload);
}

export function trackGetPurchasesAsyncFailure(
  error: Error,
  payload: Timing,
): void {
  analytics.pushError('GetPurchasesAsyncFailure', error, payload);
}

export function trackGetSignedPlayerInfoAsyncFailure(
  error: Error,
  payload: Timing & Tries,
): void {
  analytics.pushError('GetSignedPlayerInfoAsyncFailure', error, payload);
}

export function trackCanUseSquadsAsyncFailure(
  error: Error,
  payload: Timing,
): void {
  analytics.pushError('CanUseSquadsAsyncFailure', error, payload);
}

export function trackGetSquadAsyncFailure(
  error: Error,
  payload: { squadID: string } & Timing & Tries,
): void {
  analytics.pushError('GetSquadAsyncFailure', error, payload);
}

export function trackCreateSquadAsync(
  payload: AnalyticsProperties.AnyPayload,
): void {
  analytics.pushEvent('CreateSquadAsync', payload);
}

export function trackCreateSquadAsyncSuccess(
  payload: { squadID: string } & AnalyticsProperties.AnyPayload &
    Timing &
    Partial<Tries>,
): void {
  analytics.pushEvent('CreateSquadAsyncSuccess', payload);
}

export function trackCreateSquadAsyncFailure(
  error: Error,
  payload: AnalyticsProperties.AnyPayload & Timing & Partial<Tries>,
): void {
  analytics.pushError('CreateSquadAsyncFailure', error, payload);
}

export function trackJoinSquadAsync(
  payload: { squadID: string } & AnalyticsProperties.AnyPayload,
): void {
  analytics.pushEvent('JoinSquadAsync', payload);
}

export function trackJoinSquadAsyncSuccess(
  payload: { squadID: string } & AnalyticsProperties.AnyPayload & Timing,
): void {
  analytics.pushEvent('JoinSquadAsyncSuccess', payload);
}

export function trackJoinSquadAsyncFailure(
  error: Error,
  payload: { squadID: string } & AnalyticsProperties.AnyPayload & Timing,
): void {
  analytics.pushError('JoinSquadAsyncFailure', error, payload);
}

export function trackGetTournamentAsyncFailure(
  error: Error,
  payload: Timing & Tries,
): void {
  analytics.pushError('GetTournamentAsyncFailure', error, payload);
}

export function trackInviteAsync(
  payload: AnalyticsProperties.SocialEventProperties & {
    platformAPI?: PlatformAPI;
  },
): void {
  analytics.pushEvent('InviteAsync', payload);
}

export function trackInviteAsyncSuccess(
  payload: AnalyticsProperties.SocialEventProperties &
    Timing & { platformAPI?: PlatformAPI },
): void {
  analytics.pushEvent('InviteAsyncSuccess', payload);
}

export function trackInviteAsyncFailure(
  error: Error,
  payload: AnalyticsProperties.SocialEventProperties &
    Timing & { platformAPI?: PlatformAPI },
): void {
  analytics.pushError('InviteAsyncFailure', error, payload);
}

type MatchPlayerAsyncPayload = { isAsync?: boolean };

export function trackMatchPlayerAsyncStart(
  payload: MatchPlayerAsyncPayload,
): void {
  analytics.pushEvent('MatchPlayerAsyncStart', payload);
}

export function trackMatchPlayerAsyncSuccess(
  payload: MatchPlayerAsyncPayload & Timing,
): void {
  analytics.pushEvent('MatchPlayerAsyncSuccess', payload);
}

export function trackMatchPlayerAsyncFailure(
  error: Error,
  payload: MatchPlayerAsyncPayload & Timing,
): void {
  analytics.pushError('MatchPlayerAsyncFailure', error, payload);
}

export function trackPlatformInitSuccess(): void {
  analytics.pushEvent(
    'PlatformInitSuccess',
    null,
    analytics.toDefaultAndAdServer,
  );
}

export function trackPostSessionScoreAsyncSuccess(
  payload: {
    score: number;
  } & Timing,
): void {
  analytics.pushEvent('PostSessionScoreAsyncSuccess', payload);
}

export function trackPostSessionScoreAsyncFailure(
  error: Error,
  payload: {
    score: number;
  } & Timing,
): void {
  analytics.pushError('PostSessionScoreAsyncFailure', error, payload);
}

export function trackPostTournamentScoreAsyncSuccess(
  payload: {
    score: number;
  } & Timing &
    Tries,
): void {
  analytics.pushEvent('PostTournamentScoreAsyncSuccess', payload);
}

export function trackPostTournamentScoreAsyncFailure(
  error: Error,
  payload: {
    score: number;
  } & Timing &
    Tries,
): void {
  analytics.pushError('PostTournamentScoreAsyncFailure', error, payload);
}

type CreateTournamentPayload = AnalyticsProperties.SocialEventProperties & {
  title: string;
  initialScore: number;
};

export function trackCreateTournamentAsync(
  payload: CreateTournamentPayload,
): void {
  analytics.pushEvent('CreateTournamentAsync', payload);
}

export function trackCreateTournamentAsyncSuccess(
  payload: CreateTournamentPayload & Timing,
): void {
  analytics.pushEvent('CreateTournamentAsyncSuccess', payload);
}

export function trackCreateTournamentAsyncFailure(
  error: Error,
  payload: CreateTournamentPayload & Timing,
): void {
  analytics.pushError('CreateTournamentAsyncFailure', error, payload);
}

type ShareTournamentPayload = AnalyticsProperties.SocialEventProperties & {
  initialScore: number;
};

export function trackShareTournamentAsync(
  payload: ShareTournamentPayload,
): void {
  analytics.pushEvent('ShareTournamentAsync', payload);
}

export function trackShareTournamentAsyncSuccess(
  payload: ShareTournamentPayload & Timing,
): void {
  analytics.pushEvent('ShareTournamentAsyncSuccess', payload);
}

export function trackShareTournamentAsyncFailure(
  error: Error,
  payload: ShareTournamentPayload & Timing,
): void {
  analytics.pushError('ShareTournamentAsyncFailure', error, payload);
}

export function trackPayloadReadFailure(
  error: Error,
  payload: { rawPayload: string; type: 'parseFailed' },
): void {
  analytics.pushError('PayloadReadFailure', error, payload);
}

export function trackPurchaseAsyncSuccess(
  payload: FBInstant.PurchaseConfig & Timing,
): void {
  analytics.pushEvent('PurchaseAsyncSuccess', payload);
}

export function trackPurchaseAsyncFailed(
  error: Error,
  payload: FBInstant.PurchaseConfig & Timing,
): void {
  analytics.pushError('purchaseAsyncFailed', error, payload);
}

type SendMessageAsyncPayload = AnalyticsProperties.SocialEventProperties &
  Timing &
  LineUpdateAsyncPayload;

export function trackSendMessageAsyncSuccess(
  payload: SendMessageAsyncPayload & { lineImageHash: string },
): void {
  analytics.pushEvent('SendMessageAsyncSuccess', payload);
}

export function trackSendMessageAsyncFailure(
  error: Error,
  payload: SendMessageAsyncPayload,
): void {
  analytics.pushError('SendMessageAsyncFailure', error, payload);
}

export function trackSendRawAnalyticsEventFailure(
  error: Error,
  payload: Timing & Tries,
): void {
  analytics.pushError('SendRawAnalyticsEventFailure', error, payload);
}

export function trackSetDataAsyncFailed(error: Error, payload: Timing): void {
  analytics.pushError('SetDataAsyncFailed', error, payload);
}

export function trackShareAsync(
  payload: AnalyticsProperties.SocialEventProperties,
): void {
  analytics.pushEvent('ShareAsync', payload);
}

export function trackShareAsyncSuccess(
  payload: AnalyticsProperties.SocialEventProperties & {
    viberShareCount?: number;
  } & Timing,
): void {
  analytics.pushEvent('ShareAsyncSuccess', payload);
}

export function trackShareAsyncFailed(
  error: Error,
  payload: AnalyticsProperties.SocialEventProperties & Timing,
): void {
  analytics.pushError('ShareAsyncFailed', error, payload);
}

export function trackShareLinkAsyncSuccess(
  payload: AnalyticsProperties.SocialEventProperties & Timing,
): void {
  analytics.pushEvent('ShareLinkAsyncSuccess', payload);
}

export function trackShareLinkAsyncFailure(
  error: Error,
  payload: AnalyticsProperties.SocialEventProperties & Timing,
): void {
  analytics.pushError('ShareLinkAsyncFailure', error, payload);
}

type SwitchAsyncPayload = CommonAnalytics & {
  oldContextID: string;
  newContextID: string;
};

export function trackSubscribeBotAsync(
  payload: { viewCount?: number } & Partial<CommonAnalytics>,
): void {
  analytics.pushEvent('SubscribeBotAsync', payload);
}

export function trackSubscribeBotAsyncSuccess(
  payload: Timing & Partial<CommonAnalytics>,
): void {
  analytics.pushEvent('SubscribeBotAsyncSuccess', payload);
}

export function trackSubscribeBotAsyncFailure(
  error: Error,
  payload: Partial<Timing & CommonAnalytics>,
): void {
  analytics.pushError('SubscribeBotAsyncFailure', error, payload);
}

export function trackSwitchAsync(payload: SwitchAsyncPayload): void {
  analytics.pushEvent('SwitchAsync', payload);
}

export function trackSwitchAsyncSuccess(
  payload: SwitchAsyncPayload & Timing,
): void {
  analytics.pushEvent('SwitchAsyncSuccess', payload);
}

export function trackSwitchAsyncFailed(
  error: Error,
  payload: SwitchAsyncPayload & Timing,
): void {
  analytics.pushError('SwitchAsyncFailed', error, payload);
}

export function trackSwitchGameAsync(
  payload: AnalyticsProperties.AnyPayload,
): void {
  analytics.pushEvent('SwitchGameAsync', payload);
}

export function trackSwitchGameAsyncFailed(
  error: Error,
  payload: AnalyticsProperties.AnyPayload,
): void {
  analytics.pushError('SwitchGameAsyncFailed', error, payload);
}

export function trackCanSwitchNativeGameAsyncSuccess(
  payload: {
    canSwitchNativeGame: boolean;
  } & Timing,
): void {
  analytics.pushEvent('CanSwitchNativeGameAsyncSuccess', payload);
}

export function trackSwitchNativeGameAsync(
  payload: AnalyticsProperties.AnyPayload,
): void {
  analytics.pushEvent('SwitchNativeGameAsync', payload);
}

export function trackCanSwitchNativeGameAsyncFailure(
  error: Error,
  payload: Timing,
): void {
  analytics.pushError('CanSwitchNativeGameAsyncFailure', error, payload);
}

export function trackSwitchNativeGameAsyncFailure(
  error: Error,
  payload: AnalyticsProperties.AnyPayload & Timing & Tries,
): void {
  analytics.pushError('SwitchNativeGameAsyncFailure', error, payload);
}

type LineUpdateAsyncPayload = Tries & {
  isPlayer: boolean;
  platformAPI: PlatformAPI;
};

export function trackUpdateAsyncSuccess(
  payload: AnalyticsProperties.SocialEventProperties &
    Timing &
    Partial<LineUpdateAsyncPayload> & { lineImageHash?: string },
): void {
  analytics.pushEvent('UpdateAsyncSuccess', payload);
}

export function trackUpdateAsyncFailed(
  error: Error,
  payload: AnalyticsProperties.SocialEventProperties &
    Timing &
    Partial<LineUpdateAsyncPayload>,
): void {
  analytics.pushError('UpdateAsyncFailed', error, payload);
}

type UploadMessageImagePayload = AnalyticsProperties.SocialEventProperties &
  CommonAnalytics & { platformAPI: PlatformAPI };

export function trackUploadMessageImageSuccess(
  payload: UploadMessageImagePayload &
    Timing &
    Tries & { lineImageHash: string; statusCode: number },
): void {
  analytics.pushEvent('UploadMessageImageSuccess', payload);
}

export function trackUploadMessageImageFailure(
  error: Error,
  payload: UploadMessageImagePayload & Timing & Tries,
): void {
  analytics.pushError('UploadMessageImageFailure', error, payload);
}

export function trackMarketingPostbackFailure(
  error: Error,
  payload: { $adNetworkName: string },
): void {
  analytics.pushError('MarketingPostbackFailure', error, payload);
}

export * from './snapchatEvents';
export * from './zoomEvents';
